import { ChangeList, FOCUS_RING, cn, useLanguage, useTranslate, CARD_SURFACE } from '@athena/ui'

import { describeAudit } from '../audit/describe.js'
import { formatDateTime } from '../../lib/display.js'

export interface AuditEntry {
  id: string
  actorUserId: string
  actorName: string | null
  actorEmail?: string | null
  action: string
  subjectType: string
  subjectId: string
  reason: string | null
  before: string | null
  after: string | null
  createdAt: string
}

export interface AuditTrailProps {
  entries: readonly AuditEntry[]
}

/**
 * The audit trail, as a list of things that happened.
 *
 * Each entry leads with what happened ("Plan assigned"), then the fields it
 * changed in words ("Plan · Free → Premium"), who did it, why, and when.
 * That is what an administrator came for; reading the difference between two
 * blocks of JSON was not. See `audit/describe.ts` for how an entry is read.
 *
 * The raw record is kept, one deliberate click away under "View raw changes":
 * the action code, the entry's id and both snapshots exactly as stored. It is
 * the evidence when the words above are in question, and it is not the
 * default reading.
 *
 * One list with a divider per entry, not a card per entry: an audit log is
 * read top to bottom, and twenty bordered boxes made it read as twenty
 * separate things.
 */
export function AuditTrail({ entries }: AuditTrailProps) {
  const language = useLanguage()
  const t = useTranslate()

  if (entries.length === 0) {
    return <p className="text-body-sm text-text-secondary">{t('admin.auditEmpty')}</p>
  }

  return (
    <ol className={cn(CARD_SURFACE, 'divide-y divide-border')}>
      {entries.map((entry) => {
        const described = describeAudit(entry, t, language)
        return (
          <li key={entry.id} className="flex min-w-0 flex-col gap-2 px-4 py-3">
            <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5">
              <p className="text-body-sm font-semibold text-text">{described.title}</p>
              <time dateTime={entry.createdAt} className="text-caption text-text-secondary">
                {formatDateTime(entry.createdAt, language)}
              </time>
            </div>

            {described.changes.length > 0 ? (
              <ChangeList items={described.changes} />
            ) : (
              <p className="text-body-sm text-text-secondary">{t('admin.auditNoFields')}</p>
            )}

            <dl className="grid grid-cols-1 gap-x-4 gap-y-0.5 text-caption text-text-secondary sm:grid-cols-[minmax(8rem,max-content)_minmax(0,1fr)]">
              <dt className="font-semibold">{t('admin.auditActorLabel')}</dt>
              <dd className="min-w-0 break-words text-text">{described.actor}</dd>
              {entry.reason ? (
                <>
                  <dt className="font-semibold">{t('admin.auditReason')}</dt>
                  <dd className="min-w-0 break-words text-text">{entry.reason}</dd>
                </>
              ) : null}
            </dl>

            <details className="group text-caption">
              <summary
                className={cn(
                  'w-fit cursor-pointer rounded-sm font-semibold text-primary underline-offset-2 hover:underline',
                  FOCUS_RING,
                )}
              >
                {t('admin.auditDetails')}
              </summary>
              <dl className="mt-2 grid gap-2 rounded-sm bg-surface-secondary p-3">
                <div>
                  <dt className="font-semibold text-text-secondary">{t('admin.techAction')}</dt>
                  <dd className="break-all font-mono text-text">{entry.action}</dd>
                </div>
                <div>
                  <dt className="font-semibold text-text-secondary">{t('admin.techEntryId')}</dt>
                  <dd className="break-all font-mono text-text">{entry.id}</dd>
                </div>
                <div>
                  <dt className="font-semibold text-text-secondary">{t('admin.techRecord')}</dt>
                  <dd className="break-all font-mono text-text">
                    {entry.subjectType} · {entry.subjectId}
                  </dd>
                </div>
                <div>
                  <dt className="font-semibold text-text-secondary">{t('admin.auditBefore')}</dt>
                  <dd>
                    <RawJson value={entry.before} />
                  </dd>
                </div>
                <div>
                  <dt className="font-semibold text-text-secondary">{t('admin.auditAfter')}</dt>
                  <dd>
                    <RawJson value={entry.after} />
                  </dd>
                </div>
              </dl>
            </details>
          </li>
        )
      })}
    </ol>
  )
}

/** A snapshot as stored, indented. It scrolls inside its own box so the page never does. */
function RawJson({ value }: { value: string | null }) {
  let pretty = value ?? '—'
  if (value) {
    try {
      pretty = JSON.stringify(JSON.parse(value), null, 2)
    } catch {
      // Not JSON after all: shown exactly as stored.
    }
  }
  return (
    <pre className="max-h-64 max-w-full overflow-auto whitespace-pre font-mono text-caption text-text">
      {pretty}
    </pre>
  )
}
